import { Nullable } from '$models'
import { Subdivision } from './enums'

/**
 * Display name for each ISO 3166-2 subdivision Loaded supports (AU states/territories and NZ
 * regions). Keyed by the SDK `Subdivision` enum, whose runtime values are the hyphenated ISO codes
 * ("AU-VIC") — so at runtime this reads as a code→label map. Record<Subdivision, string> makes
 * additions to the backend enum a compile error here. Labels MUST be kept in lockstep with the
 * app_admin copy (app_admin/modules/company/subdivisions.constant.js).
 */
export const SUBDIVISION_LABEL_BY_CODE: Readonly<Record<Subdivision, string>> =
  {
    [Subdivision.AUACT]: 'Australian Capital Territory',
    [Subdivision.AUNSW]: 'New South Wales',
    [Subdivision.AUNT]: 'Northern Territory',
    [Subdivision.AUQLD]: 'Queensland',
    [Subdivision.AUSA]: 'South Australia',
    [Subdivision.AUTAS]: 'Tasmania',
    [Subdivision.AUVIC]: 'Victoria',
    [Subdivision.AUWA]: 'Western Australia',
    [Subdivision.NZAUK]: 'Auckland',
    [Subdivision.NZBOP]: 'Bay of Plenty',
    [Subdivision.NZCAN]: 'Canterbury',
    [Subdivision.NZCIT]: 'Chatham Islands',
    [Subdivision.NZGIS]: 'Gisborne',
    [Subdivision.NZHKB]: "Hawke's Bay",
    [Subdivision.NZMBH]: 'Marlborough',
    [Subdivision.NZMWT]: 'Manawatū-Whanganui',
    [Subdivision.NZNSN]: 'Nelson',
    [Subdivision.NZNTL]: 'Northland',
    [Subdivision.NZOTA]: 'Otago',
    [Subdivision.NZSTL]: 'Southland',
    [Subdivision.NZTAS]: 'Tasman',
    [Subdivision.NZTKI]: 'Taranaki',
    [Subdivision.NZWGN]: 'Wellington',
    [Subdivision.NZWKO]: 'Waikato',
    [Subdivision.NZWTC]: 'West Coast',
  }

/**
 * Spellings that don't normalize to a {@link SUBDIVISION_LABEL_BY_CODE} label but still appear in
 * address data (legacy names, official long forms).
 */
const SUBDIVISION_NAME_ALIASES: Readonly<Record<string, string>> = {
  'manawatu-wanganui': 'NZ-MWT',
  'chatham islands territory': 'NZ-CIT',
}

/**
 * Normalizes a subdivision display name for lookup: case/whitespace-insensitive, apostrophes and
 * diacritics (macrons) removed, and Google's "<name> Region"/"<name> District" suffixes stripped.
 */
const normalizeSubdivisionName = (name: string): string =>
  name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['’]/g, '')
    .replace(/\s+(region|district)$/, '')

const SUBDIVISION_CODE_BY_NORMALIZED_NAME: Readonly<Record<string, string>> = {
  ...Object.fromEntries(
    Object.entries(SUBDIVISION_LABEL_BY_CODE).map(([code, label]) => [
      normalizeSubdivisionName(label),
      code,
    ])
  ),
  ...SUBDIVISION_NAME_ALIASES,
}

/**
 * Maps a state/region display name (as returned by the Places lookup, e.g. "Victoria" or
 * "Otago Region") to its ISO 3166-2 subdivision code ("AU-VIC", "NZ-OTA"). Returns null for
 * unrecognized names — venues in unsupported countries store no subdivision.
 */
export const mapPlaceStateToSubdivision = (
  state: Nullable<string> | undefined
): Subdivision | null => {
  if (!state) {
    return null
  }

  // The lookup values originate from the enum's runtime string values, so the assertion is sound
  // (via unknown because the SDK declares the enum numerically despite the string runtime values).
  return (SUBDIVISION_CODE_BY_NORMALIZED_NAME[
    normalizeSubdivisionName(state)
  ] ?? null) as unknown as Subdivision | null
}
